import { startTransition, useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
    TrendingUp,
    Users,
    CheckCircle,
    UserCheck,
    AlarmClock,
    X,
    Briefcase,
    UserCircle2,
} from "lucide-react";
import { getHighlightsDailySeries, getMyHighlights } from "../../services/highlights.service";
import { HighlightsTrendLineChart } from "./HighlightsTrendLineChart";

export type HighlightMetricId =
    | "approaches"
    | "scheduled"
    | "joins"
    | "selections"
    | "subJobs"
    | "attended"
    | "tomorrow"
    | "users";

export interface HighlightsAggregateData {
    totalApproaches: number;
    totalWalkings: number;
    totalJoins: number;
    totalSelections: number;
    scheduledForTomorrow: number;
    totalSubJobs: number;
    totalAttended: number;
    userCount: number;
}

const METRIC_ORDER: HighlightMetricId[] = [
    "approaches",
    "scheduled",
    "joins",
    "selections",
    "subJobs",
    "attended",
    "tomorrow",
    "users",
];

function firstVisibleMetricId(
    excluded?: readonly HighlightMetricId[],
    hideUsersTile?: boolean
): HighlightMetricId | null {
    const ex = new Set(excluded ?? []);
    if (hideUsersTile) ex.add("users");
    for (const id of METRIC_ORDER) {
        if (!ex.has(id)) return id;
    }
    return null;
}

export interface HighlightsStatsSectionProps {
    startDate: string;
    endDate: string;
    onStartDateChange: (value: string) => void;
    onEndDateChange: (value: string) => void;
    cardTitle?: string;
    /** Explains difference between top cards vs table totals (Highlights page only). */
    showAggregateHints?: boolean;
    /** When true, tiles open a trend chart below (per-day series). */
    showTrendChart?: boolean;
    /** Tiles matching these ids are not rendered (e.g. investor dashboard). */
    excludedMetricIds?: readonly HighlightMetricId[];
    /** When true, show Total Users tile and load userCount (investor dashboard only). */
    showUsersMetric?: boolean;
}

const MAX_CHART_DAYS = 365;

function parseLocalYMD(s: string): Date {
    const [y, m, d] = s.split("-").map((x) => parseInt(x, 10));
    return new Date(y, m - 1, d);
}

function formatLocalYMD(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function enumerateDaysInclusive(startStr: string, endStr: string): string[] {
    const start = parseLocalYMD(startStr);
    const end = parseLocalYMD(endStr);
    if (start > end) return [];
    const days: string[] = [];
    const cur = new Date(start);
    let count = 0;
    while (cur <= end && count < MAX_CHART_DAYS) {
        days.push(formatLocalYMD(cur));
        cur.setDate(cur.getDate() + 1);
        count++;
    }
    return days;
}

function lastNDaysLocal(n: number): string[] {
    const days: string[] = [];
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    for (let i = n - 1; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        days.push(formatLocalYMD(d));
    }
    return days;
}

function chartDaysFromFilters(startDate: string, endDate: string): string[] {
    if (startDate && endDate) {
        return enumerateDaysInclusive(startDate, endDate);
    }
    if (startDate && !endDate) {
        return [startDate];
    }
    if (!startDate && endDate) {
        return [endDate];
    }
    return lastNDaysLocal(7);
}

function formatChartAxisLabel(day: string): string {
    const d = parseLocalYMD(day);
    return d.toLocaleDateString("en-IN", { month: "short", day: "numeric" });
}

export function HighlightsStatsSection({
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    cardTitle = "Your Highlights",
    showAggregateHints = true,
    showTrendChart = true,
    excludedMetricIds,
    showUsersMetric = false,
}: HighlightsStatsSectionProps) {
    const [highlights, setHighlights] = useState<HighlightsAggregateData>({
        totalApproaches: 0,
        totalWalkings: 0,
        totalJoins: 0,
        totalSelections: 0,
        scheduledForTomorrow: 0,
        totalSubJobs: 0,
        totalAttended: 0,
        userCount: 0,
    });
    const [loading, setLoading] = useState(false);
    const [tileError, setTileError] = useState<string | null>(null);

    const [selectedMetricId, setSelectedMetricId] = useState<HighlightMetricId | null>(() =>
        firstVisibleMetricId(excludedMetricIds, !showUsersMetric)
    );
    const [chartRows, setChartRows] = useState<{ label: string; value: number }[]>([]);
    const [seriesLoading, setSeriesLoading] = useState(false);
    const [seriesError, setSeriesError] = useState<string | null>(null);

    const fetchMyHighlights = useCallback(async () => {
        setLoading(true);
        setTileError(null);
        try {
            const dateFrom = startDate || undefined;
            const dateTo = endDate || undefined;
            const response = await getMyHighlights(dateFrom, dateTo);
            if (response?.status === "success") {
                setHighlights({
                    totalApproaches: response.data.totalApproaches || 0,
                    totalWalkings: response.data.walkins || 0,
                    totalJoins: response.data.Joined || 0,
                    totalSelections: response.data.Selected || 0,
                    totalSubJobs: response.data.totalSubJobs || 0,
                    scheduledForTomorrow: response.data.scheduledForTomorrow || 0,
                    totalAttended: response.data.attended || 0,
                    userCount: showUsersMetric ? (response.data.userCount ?? 0) : 0,
                });
            }
        } catch (err) {
            console.error("Failed to load highlights:", err);
            setTileError("Failed to load highlights. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, showUsersMetric]);

    useEffect(() => {
        fetchMyHighlights();
    }, [fetchMyHighlights]);

    const handleClearFilters = () => {
        onStartDateChange("");
        onEndDateChange("");
    };

    const statsItems = useMemo(
        () =>
            [
                {
                    id: "approaches" as const,
                    key: "Total Approaches",
                    value: highlights.totalApproaches,
                    icon: <Users className="h-5 w-5" />,
                    color: "text-blue-600",
                },
                {
                    id: "scheduled" as const,
                    key: "Total Scheduled",
                    value: highlights.totalWalkings,
                    icon: <TrendingUp className="h-5 w-5" />,
                    color: "text-orange-600",
                },
                {
                    id: "joins" as const,
                    key: "Total Joins",
                    value: highlights.totalJoins,
                    icon: <UserCheck className="h-5 w-5" />,
                    color: "text-green-600",
                },
                {
                    id: "selections" as const,
                    key: "Total Selections",
                    value: highlights.totalSelections,
                    icon: <CheckCircle className="h-5 w-5" />,
                    color: "text-purple-600",
                },
                {
                    id: "subJobs" as const,
                    key: "Sub Jobs posts",
                    value: highlights.totalSubJobs,
                    icon: <Briefcase className="h-5 w-5" />,
                    color: "text-purple-600",
                },
                {
                    id: "attended" as const,
                    key: "Total Attended",
                    value: highlights.totalAttended,
                    icon: <UserCheck className="h-5 w-5" />,
                    color: "text-green-600",
                },
                {
                    id: "tomorrow" as const,
                    key: "Scheduled for Tomorrow",
                    value: highlights.scheduledForTomorrow,
                    icon: <AlarmClock className="h-5 w-5" />,
                    color: "text-purple-600",
                },
                {
                    id: "users" as const,
                    key: "Total Users",
                    value: highlights.userCount,
                    icon: <UserCircle2 className="h-5 w-5" />,
                    color: "text-sky-600",
                },
            ] as const,
        [highlights]
    );

    const excludedEffective = useMemo(() => {
        const s = new Set(excludedMetricIds ?? []);
        if (!showUsersMetric) s.add("users");
        return s;
    }, [excludedMetricIds, showUsersMetric]);

    const visibleStatsItems = useMemo(
        () => statsItems.filter((item) => !excludedEffective.has(item.id)),
        [statsItems, excludedEffective]
    );

    /** Match column count to tiles so sparse dashboards (e.g. investor) use full width. */
    const statsTileGridClass = useMemo(() => {
        const n = visibleStatsItems.length;
        if (n >= 6) return "grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6";
        if (n === 5) return "grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5";
        if (n === 4) return "grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4";
        if (n === 3) return "grid grid-cols-1 gap-4 md:grid-cols-3";
        if (n === 2) return "grid grid-cols-1 gap-4 sm:grid-cols-2";
        return "grid grid-cols-1 gap-4";
    }, [visibleStatsItems.length]);

    const statsTileComfortLayout = visibleStatsItems.length <= 3;

    useEffect(() => {
        if (selectedMetricId && excludedEffective.has(selectedMetricId)) {
            setSelectedMetricId(firstVisibleMetricId(excludedMetricIds, !showUsersMetric));
        }
    }, [excludedEffective, excludedMetricIds, selectedMetricId, showUsersMetric]);

    useEffect(() => {
        if (!showTrendChart || selectedMetricId !== "tomorrow") return;
        setSeriesLoading(false);
        setSeriesError(null);
        setChartRows([
            {
                label: "Next interview window",
                value: highlights.scheduledForTomorrow,
            },
        ]);
    }, [showTrendChart, selectedMetricId, highlights.scheduledForTomorrow]);

    useEffect(() => {
        if (!showTrendChart || !selectedMetricId || selectedMetricId === "tomorrow") {
            return;
        }

        let cancelled = false;
        const days = chartDaysFromFilters(startDate, endDate);
        const rangeFrom = days[0];
        const rangeTo = days.length ? days[days.length - 1] : undefined;

        (async () => {
            if (!rangeFrom || !rangeTo) {
                setSeriesLoading(false);
                setChartRows([]);
                return;
            }

            setSeriesLoading(true);
            setSeriesError(null);
            try {
                const res = await getHighlightsDailySeries(selectedMetricId, rangeFrom, rangeTo);
                if (cancelled) return;
                if (res?.status === "success" && Array.isArray(res.data?.series)) {
                    const mapped = res.data.series.map(
                        (row: { day: string; value: number }) => ({
                            label: formatChartAxisLabel(row.day),
                            value: row.value,
                        })
                    );
                    startTransition(() => {
                        if (!cancelled) setChartRows(mapped);
                    });
                } else {
                    startTransition(() => {
                        if (!cancelled) setChartRows([]);
                    });
                }
            } catch (e) {
                console.error(e);
                if (!cancelled) {
                    setSeriesError("Could not load chart data.");
                    startTransition(() => setChartRows([]));
                }
            } finally {
                if (!cancelled) setSeriesLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [showTrendChart, selectedMetricId, startDate, endDate]);

    useEffect(() => {
        if (!showTrendChart || !selectedMetricId) {
            setChartRows([]);
            setSeriesError(null);
            setSeriesLoading(false);
        }
    }, [showTrendChart, selectedMetricId]);

    const selectedLabel = useMemo(() => {
        const item = visibleStatsItems.find((s) => s.id === selectedMetricId);
        return item?.key ?? "";
    }, [visibleStatsItems, selectedMetricId]);

    const handleTileClick = (id: HighlightMetricId) => {
        if (!showTrendChart) return;
        setSelectedMetricId((prev) => (prev === id ? null : id));
    };

    return (
        <section className="mb-6">
            {tileError && (
                <div className="mb-4 rounded-lg border-2 border-red-200 bg-red-50 p-4 shadow-sm">
                    <p className="text-sm font-semibold text-red-900">{tileError}</p>
                </div>
            )}
            <Card className="border border-gray-200 bg-white shadow-sm">
                <CardHeader className="border-b border-gray-200 bg-gray-50/80 pb-4">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <CardTitle className="text-lg font-bold text-gray-900">{cardTitle}</CardTitle>
                        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
                            <Input
                                type="date"
                                value={startDate}
                                onChange={(e) => onStartDateChange(e.target.value)}
                                max={endDate || undefined}
                                className="h-9 w-full rounded-lg border-gray-200 bg-white text-sm focus:ring-2 focus:ring-orange-500 sm:w-auto"
                                placeholder="Start Date"
                            />
                            <Input
                                type="date"
                                value={endDate}
                                onChange={(e) => onEndDateChange(e.target.value)}
                                min={startDate || undefined}
                                className="h-9 w-full rounded-lg border-gray-200 bg-white text-sm focus:ring-2 focus:ring-orange-500 sm:w-auto"
                                placeholder="End Date"
                            />
                            {(startDate || endDate) && (
                                <Button
                                    onClick={handleClearFilters}
                                    variant="outline"
                                    size="sm"
                                    className="h-9 rounded-lg border-gray-200 px-3 focus:ring-2 focus:ring-orange-500"
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="py-6">
                    {loading ? (
                        <div className={statsTileGridClass}>
                            {Array.from({
                                length: Math.max(visibleStatsItems.length, statsTileComfortLayout ? 3 : 4),
                            }).map((_, idx) => (
                                <div
                                    key={idx}
                                    className={`animate-pulse rounded-lg bg-gradient-to-r from-gray-200 to-gray-300 ${
                                        statsTileComfortLayout ? "h-32 md:h-36" : "h-24"
                                    }`}
                                />
                            ))}
                        </div>
                    ) : (
                        <>
                            {showAggregateHints && (
                                <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50/70 px-3 py-2 text-xs text-blue-900">
                                    <div>
                                        <span className="font-semibold">Top cards:</span> overall totals for the
                                        selected date range (default:{" "}
                                        <span className="font-semibold">full current calendar month</span>; if you clear
                                        dates, totals follow server rules for “today”), including hierarchical reporting
                                        users (not only same-level users).
                                    </div>
                                    <div className="mt-1">
                                        <span className="font-semibold">Table totals:</span> totals after table
                                        filters (search, role, manager, sorting) are applied.
                                    </div>
                                </div>
                            )}
                            {showTrendChart && (
                                <p className="mb-3 text-xs text-gray-600">
                                    Click a metric to show its trend below. Click again to hide.
                                </p>
                            )}
                            <div className={statsTileGridClass}>
                                {visibleStatsItems.map((item) => {
                                    const isSelected = showTrendChart && selectedMetricId === item.id;
                                    return (
                                        <button
                                            key={item.id}
                                            type="button"
                                            onClick={() => handleTileClick(item.id)}
                                            disabled={!showTrendChart}
                                            className={`flex border-2 bg-white text-left shadow-sm ${
                                                statsTileComfortLayout
                                                    ? "gap-5 rounded-xl p-5 min-h-[7.25rem] md:min-h-[7.75rem] md:p-6 [&_svg]:h-6 [&_svg]:w-6 md:[&_svg]:h-7 md:[&_svg]:w-7"
                                                    : "gap-4 rounded-lg p-4"
                                            } ${
                                                showTrendChart
                                                    ? "cursor-pointer focus-visible:outline focus-visible:ring-2 focus-visible:ring-[#EC6E51] focus-visible:ring-offset-2"
                                                    : ""
                                            } ${
                                                isSelected
                                                    ? "border-[#EC6E51] bg-[#FFF5F2] ring-2 ring-[#EC6E51]/30"
                                                    : "border-gray-200"
                                            }`}
                                            aria-pressed={isSelected}
                                        >
                                            <div className={`flex-shrink-0 ${item.color}`}>{item.icon}</div>
                                            <div className="min-w-0 flex-1">
                                                <div
                                                    className={`mb-1 font-medium text-gray-600 ${
                                                        statsTileComfortLayout
                                                            ? "text-sm md:text-base"
                                                            : "text-xs"
                                                    }`}
                                                >
                                                    {item.key}
                                                </div>
                                                <div
                                                    className={`font-bold text-gray-900 ${
                                                        statsTileComfortLayout
                                                            ? "text-3xl tabular-nums md:text-4xl"
                                                            : "text-midi text-2xl"
                                                    }`}
                                                >
                                                    {item.value.toLocaleString("en-IN")}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {showTrendChart && selectedMetricId && (
                <Card className="mt-4 border border-gray-200 bg-white shadow-sm">
                    <CardHeader className="border-b border-gray-200 pb-3">
                        <CardTitle className="text-base font-bold text-gray-900">
                            Trend — {selectedLabel}
                        </CardTitle>
                        {selectedMetricId === "tomorrow" ? (
                            <p className="text-xs text-gray-600">
                                Count for interviews scheduled in the next calendar window (not split by day).
                            </p>
                        ) : selectedMetricId === "users" ? (
                            <p className="text-xs text-gray-600">
                                The tile shows total users in your scope (not filtered by the date range). The chart is
                                new accounts created per day in the selected range (or last 7 days when dates are
                                cleared).
                            </p>
                        ) : (
                            <p className="text-xs text-gray-600">
                                {startDate && endDate
                                    ? `One value per day in the selected range (max ${MAX_CHART_DAYS} days).`
                                    : "Pick a start and end date for a daily trend, or use Clear and we fall back to the last 7 days."}
                            </p>
                        )}
                    </CardHeader>
                    <CardContent className="py-4">
                        {seriesError && (
                            <p className="mb-2 text-sm text-red-600">{seriesError}</p>
                        )}
                        {seriesLoading ? (
                            <div className="h-72 animate-pulse rounded-lg bg-gradient-to-r from-gray-200 to-gray-300" />
                        ) : chartRows.length === 0 ? (
                            <p className="py-8 text-center text-sm text-gray-500">No data for this range.</p>
                        ) : (
                            <HighlightsTrendLineChart rows={chartRows} datasetLabel={selectedLabel} />
                        )}
                    </CardContent>
                </Card>
            )}
        </section>
    );
}
